import { getAttribution } from '@/utils/tracking/attribution';

/**
 * GA4 event names sent to dataLayer (complete_registration, start_trial, purchase).
 */
export const GA4_EVENT = {
  COMPLETE_REGISTRATION: 'complete_registration',
  START_TRIAL: 'start_trial',
  PURCHASE: 'purchase',
};

const GOOGLE_CLICK_PARAMS = ['gclid', 'wbraid', 'gbraid'];

function getClickIdParams(touch) {
  if (!touch || typeof touch !== 'object') return {};
  const out = {};
  for (const param of GOOGLE_CLICK_PARAMS) {
    if (touch[param]) out[param] = touch[param];
  }
  if (touch.keyword) out.keyword = touch.keyword;
  if (touch.mt) out.mt = touch.mt;
  return out;
}

function generateEventId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Push a conversion event to dataLayer for Google (GTM/GA4). Fires for every conversion;
 * attribution (gclid, wbraid, gbraid) from our store is attached when present.
 *
 * @param {Object} options
 * @param {string} options.event - GA4 event name: complete_registration | start_trial | purchase
 * @param {string} [options.eventId] - Deduplication ID (auto-generated if not provided)
 * @param {string} [options.transactionId] - Transaction ID (e.g. subscription id for purchase)
 * @param {number} [options.value] - Amount (e.g. subscription value)
 * @param {string} [options.currency='USD']
 * @param {Object} [options.metadata] - Extra params (plan, tier, subscription_id for purchase)
 * @returns {boolean} - True if pushed
 */
export function pushConversionToDataLayer({
  event,
  eventId,
  transactionId,
  value,
  currency = 'USD',
  metadata = {},
}) {
  if (typeof window === 'undefined') return false;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }

  const lastTouch = getAttribution()?.last_touch;
  const clickParams = getClickIdParams(lastTouch);

  const params = {
    event_id: eventId ?? generateEventId(),
    value: value ?? 0,
    currency,
    ...clickParams,
    ...(transactionId != null && { transaction_id: transactionId }),
    ...metadata,
  };

  try {
    window.dataLayer.push({ event, ...params });
  } catch (e) {
    console.warn('dataLayer.push failed', e);
    return false;
  }

  return true;
}

/**
 * Push complete_registration (sign up). Call after successful account creation.
 * Fires for every sign-up; attribution from our store is attached when present.
 */
export function pushCompleteRegistration() {
  return pushConversionToDataLayer({
    event: GA4_EVENT.COMPLETE_REGISTRATION,
    value: 0,
    currency: 'USD',
  });
}

/**
 * Push start_trial (user connected new shop). Call when OAuth/connect flow completes.
 */
export function pushStartTrial() {
  return pushConversionToDataLayer({
    event: GA4_EVENT.START_TRIAL,
    value: 0,
    currency: 'USD',
  });
}

/**
 * Push purchase (subscription created). Call when subscription confirm returns success.
 *
 * @param {Object} options
 * @param {string} options.transactionId - Subscription or shop ID
 * @param {number} options.value - Amount in dollars
 * @param {string} [options.currency='USD']
 * @param {string} [options.plan]
 * @param {string} [options.tier]
 * @param {string} [options.subscriptionId]
 */
export function pushPurchase({ transactionId, value, currency = 'USD', plan, tier, subscriptionId }) {
  const metadata = {};
  if (plan) metadata.plan = plan;
  if (tier) metadata.tier = tier;
  if (subscriptionId) metadata.subscription_id = subscriptionId;

  return pushConversionToDataLayer({
    event: GA4_EVENT.PURCHASE,
    eventId: subscriptionId != null ? String(subscriptionId) : undefined,
    transactionId,
    value,
    currency,
    metadata,
  });
}
